import React, { useEffect, useState } from "react";
import { Box, TextField, Button, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchRechargeReport } from "../../Redux/Reducers/CallAndChatReducer";
import toast from "react-hot-toast";

function DailyRechargeReport() {
  const dispatch = useDispatch();
  const RechargeData = useSelector((state) => state?.CallAndChatReducer || {});
  const { data: rowsData = [], paginationDetail = {}, status = "idle" } = RechargeData;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,  
    pageSize: 10,
  });

  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }
    
    const pageToSend = paginationModel.page + 1; 
    const params = {
      page: pageToSend,
      limit: paginationModel.pageSize,
    };
    
    // Add date filters if applied
    if (appliedStartDate) {
      params.startDate = appliedStartDate;
    }
    if (appliedEndDate) {
      params.endDate = appliedEndDate;
    }
    
    dispatch(fetchRechargeReport(params));
  }, [dispatch, paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  const handleSearch = () => {
    if (!searchValue.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchRechargeReport({ page: 1, limit: 10 }));
      return;
    }
    setAppliedSearch(searchValue);
    // Call API with only search parameter (no page/limit)
    dispatch(fetchRechargeReport({ search: searchValue }));
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchRechargeReport({ page: 1, limit: 10 }));
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchValue("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setAppliedSearch("");
    setPaginationModel({ page: 0, pageSize: 10 });
    // Reload without filters
    dispatch(fetchRechargeReport({ page: 1, limit: 10 }));
  };

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "fullName", headerName: "User Name", width: 150 },
    { field: "mobileNumber", headerName: "Mobile No.", width: 140 },
    { field: "openingBalance", headerName: "Opening Balance", width: 150 },
    { field: "rechargeAmount", headerName: "Recharge Amount", width: 150 },
    { field: "promoAmount", headerName: "Promo Amount", width: 130 },
    { field: "invoiceAmount", headerName: "Invoice Amount", width: 150 },
    { field: "gst", headerName: "GST", width: 100 },
    { field: "totalWallet", headerName: "Total Wallet", width: 150 },
   
    { field: "invoiceNumber", headerName: "Invoice No", width: 130 },
    { field: "createdAt", headerName: "Created At", width: 130 },
    { field: "updatedAt", headerName: "Updated At", width: 130 },
    { field: "paymentStatus", headerName: "Payment Status", width: 130 },

  
  ];

  const rows = rowsData?.map((item, index) => ({
    id: item._id || item.id || index,
    sono: paginationModel.page * paginationModel.pageSize + index + 1, 
    fullName: item?.fullName || "N/A",
    mobileNumber: item?.mobileNumber || "N/A",
    openingBalance: item?.openingBalance || 0,
    rechargeAmount: item?.rechargeAmount || 0,
    promoAmount: item?.promoAmount || 0,
    totalWallet: item?.totalWallet || 0,
    gst: item?.gst || 0,
    invoiceNumber: item?.invoiceNumber || "N/A",
    invoiceAmount: item?.invoiceAmount || 0,
    paymentStatus: item?.paymentStatus,
    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString()
    : "",
updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleString()
    : "",
  }));

  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch 
    ? (rowsData?.length || 0)
    : (paginationDetail?.totalRecords || 0);
  

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Recharge Report" />
        
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, compact format */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    {/* Search on left */}
                    <Box display="flex" alignItems="flex-end" gap={1}>
                      <TextField
                        placeholder="Search..."
                        value={searchValue}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ width: '250px' }}
                      />
                      <IconButton
                        onClick={handleSearch}
                        disabled={status === "loading" || !searchValue.trim()}
                        sx={{ 
                          height: '40px',
                          width: '40px',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '&:disabled': {
                            backgroundColor: 'action.disabledBackground',
                            color: 'action.disabled',
                          }
                        }}
                      >
                        <FaSearch />
                      </IconButton>
                    </Box>
                    
                    {/* Date filters on right */}
                    <Box display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
                      <TextField
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleApplyFilter}
                        disabled={status === "loading"}
                        sx={{ height: '40px' }}
                      >
                        {status === "loading" ? "Loading..." : "Apply"}
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleClearFilters}
                        sx={{ height: '40px' }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      disableSelectionOnClick
                      loading={status === "loading"}
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyRechargeReport;
